import React, { useState, useEffect } from 'react';
import axios from '../services/axiosConfig';
import HistorialService from '../services/historiales';
import { notificationSystem } from '../utils/notifications';
import '../styles/historial.css';
import NotasSesion from './NotasSesion';
import Diagnosticos from './Diagnosticos';
import Evoluciones from './Evoluciones';
import Antecedentes from './Antecedentes';
import ObjetivosTerapeuticos from './ObjetivosTerapeuticos';

function HistorialClinico({ paciente_id: propPacienteId, paciente_nombre: propPacienteNombre }) {
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(propPacienteId || null);
  const [nombrePaciente, setNombrePaciente] = useState(propPacienteNombre || '');
  const [pacientes, setPacientes] = useState([]);
  const [busquedaPaciente, setBusquedaPaciente] = useState('');
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [activeTab, setActiveTab] = useState('notas');
  const [notas, setNotas] = useState([]);
  const [diagnosticos, setDiagnosticos] = useState([]);
  const [evoluciones, setEvoluciones] = useState([]);
  const [antecedentes, setAntecedentes] = useState({});
  const [objetivos, setObjetivos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    cargarPacientes();
  }, []);

  useEffect(() => {
    if (pacienteSeleccionado) {
      cargarHistorial();
    }
  }, [pacienteSeleccionado]);

  const cargarPacientes = async () => {
    try {
      const response = await axios.get('/pacientes');
      const pacientesData = response.data;
      setPacientes(Array.isArray(pacientesData) ? pacientesData : (pacientesData?.data || []));
    } catch (error) {
      console.error('Error cargando pacientes:', error);
    }
  };

  const seleccionarPaciente = (paciente) => {
    setPacienteSeleccionado(paciente.id);
    setNombrePaciente(paciente.nombre);
    setBusquedaPaciente(paciente.nombre);
    setMostrarResultados(false);
  };

  const pacientesFiltrados = pacientes.filter(p => 
    busquedaPaciente && (
      p.nombre?.toLowerCase().includes(busquedaPaciente.toLowerCase()) ||
      p.email?.toLowerCase().includes(busquedaPaciente.toLowerCase()) ||
      p.telefono?.includes(busquedaPaciente)
    )
  );

  const cargarHistorial = async () => {
    if (!pacienteSeleccionado) return;
    
    try {
      setLoading(true);
      const [notasRes, diagRes, evolRes, antRes, objRes] = await Promise.all([
        HistorialService.obtenerNotasSesion(pacienteSeleccionado),
        HistorialService.obtenerDiagnosticos(pacienteSeleccionado),
        HistorialService.obtenerEvoluciones(pacienteSeleccionado),
        HistorialService.obtenerAntecedentes(pacienteSeleccionado),
        HistorialService.obtenerObjetivos(pacienteSeleccionado)
      ]);

      setNotas(notasRes?.data?.data || []);
      setDiagnosticos(diagRes?.data?.data || []);
      setEvoluciones(evolRes?.data?.data || []);
      setAntecedentes(antRes?.data?.data || {});
      setObjetivos(objRes?.data?.data || []);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      notificationSystem.error('Error al cargar el historial clínico');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="historial-container">
      <h2>📋 Historial Clínico</h2>
      
      {!pacienteSeleccionado ? (
        <div className="seleccionar-paciente">
          <h3>Selecciona un paciente para ver su historial</h3>
          <div className="busqueda-paciente-container">
            <div className="input-con-resultados">
              <input
                type="text"
                placeholder="🔍 Buscar paciente por nombre, email o teléfono..."
                value={busquedaPaciente}
                onChange={(e) => {
                  setBusquedaPaciente(e.target.value);
                  setMostrarResultados(true);
                }}
                onFocus={() => busquedaPaciente && setMostrarResultados(true)}
              />
              
              {mostrarResultados && busquedaPaciente && (
                <div className="resultados-busqueda">
                  {pacientesFiltrados.length > 0 ? (
                    pacientesFiltrados.slice(0, 5).map(paciente => (
                      <div 
                        key={paciente.id}
                        className="resultado-item"
                        onClick={() => seleccionarPaciente(paciente)}
                      >
                        <div className="resultado-nombre">{paciente.nombre}</div>
                        <div className="resultado-info">
                          {paciente.email} • {paciente.telefono}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-resultados">
                      No se encontraron pacientes
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="paciente-header">
            <h3>Paciente: {nombrePaciente}</h3>
            <button 
              className="btn-cambiar-paciente"
              onClick={() => {
                setPacienteSeleccionado(null);
                setNombrePaciente('');
                setBusquedaPaciente('');
                setNotas([]);
                setDiagnosticos([]);
                setEvoluciones([]);
                setAntecedentes({});
                setObjetivos([]);
              }}
            >
              Cambiar Paciente
            </button>
          </div>
          
          <div className="historial-tabs">
            <button 
              className={activeTab === 'notas' ? 'active' : ''} 
              onClick={() => setActiveTab('notas')}
            >
              📝 Notas de Sesión
            </button>
            <button 
              className={activeTab === 'diagnosticos' ? 'active' : ''} 
              onClick={() => setActiveTab('diagnosticos')}
            >
              🔍 Diagnósticos
            </button>
            <button 
              className={activeTab === 'evoluciones' ? 'active' : ''} 
              onClick={() => setActiveTab('evoluciones')}
            >
              📈 Evoluciones
            </button>
            <button 
              className={activeTab === 'antecedentes' ? 'active' : ''} 
              onClick={() => setActiveTab('antecedentes')}
            >
              📋 Antecedentes
            </button>
            <button 
              className={activeTab === 'objetivos' ? 'active' : ''} 
              onClick={() => setActiveTab('objetivos')}
            >
              🎯 Objetivos
            </button>
          </div>

          {loading ? (
            <div className="loading">Cargando historial...</div>
          ) : (
            <div className="historial-content">
              {activeTab === 'notas' && <NotasSesion notas={notas} paciente_id={pacienteSeleccionado} onRefresh={cargarHistorial} />}
              {activeTab === 'diagnosticos' && <Diagnosticos diagnosticos={diagnosticos} paciente_id={pacienteSeleccionado} onRefresh={cargarHistorial} />}
              {activeTab === 'evoluciones' && <Evoluciones evoluciones={evoluciones} paciente_id={pacienteSeleccionado} onRefresh={cargarHistorial} />}
              {activeTab === 'antecedentes' && <Antecedentes antecedentes={antecedentes} paciente_id={pacienteSeleccionado} onRefresh={cargarHistorial} />}
              {activeTab === 'objetivos' && <ObjetivosTerapeuticos objetivos={objetivos} paciente_id={pacienteSeleccionado} onRefresh={cargarHistorial} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HistorialClinico;
